import { useEffect, useMemo, useState, type FormEvent, type MouseEvent } from 'react';
import {
  Check,
  Clock3,
  FilePenLine,
  MessageSquareText,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
  UserRound,
  X
} from 'lucide-react';
import { readToken, tenantTokenKey } from '../../app/session';
import { ApiError, apiRequest } from '../../lib/api';
import { formatDateTimeValue } from '../../lib/croatian-date';
import { useTenantApp } from '../app/TenantAppContext';

interface NoteItem {
  id: string;
  title: string;
  body: string;
  status: 'PENDING' | 'PUBLISHED' | 'REJECTED';
  authorId: string;
  authorName: string;
  reviewerName: string | null;
  rejectionReason: string | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  canEdit: boolean;
  canDelete: boolean;
}

const emptyForm = { title: '', body: '' };

function noteStatusLabel(status: NoteItem['status']) {
  return {
    PENDING: 'Čeka odobrenje',
    PUBLISHED: 'Objavljeno',
    REJECTED: 'Vraćeno na doradu'
  }[status];
}

export function NotesPanel() {
  const { context } = useTenantApp();
  const canCreate = context.user.permissions.includes('notes.create');
  const [notes, setNotes] = useState<NoteItem[]>([]);
  const [canModerate, setCanModerate] = useState(false);
  const [status, setStatus] = useState<'ALL' | NoteItem['status']>('ALL');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState<NoteItem | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [rejecting, setRejecting] = useState<NoteItem | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  async function load() {
    try {
      const query = new URLSearchParams({ status, search });
      const response = await apiRequest<{ canModerate: boolean; items: NoteItem[] }>(
        `/api/v1/notes?${query}`,
        { accessToken: readToken(tenantTokenKey) }
      );
      setNotes(response.items);
      setCanModerate(response.canModerate);
      setError('');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Bilješke nisu dostupne.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const delay = window.setTimeout(() => void load(), 160);
    return () => window.clearTimeout(delay);
  }, [search, status]);

  const summary = useMemo(() => ({
    published: notes.filter((note) => note.status === 'PUBLISHED').length,
    pending: notes.filter((note) => note.status === 'PENDING').length,
    mine: notes.filter((note) => note.authorId === context.user.id).length
  }), [context.user.id, notes]);

  function openNew() {
    setEditing(null);
    setForm(emptyForm);
    setError('');
    setModal(true);
  }

  function openNote(note: NoteItem) {
    setEditing(note);
    setForm({ title: note.title, body: note.body });
    setError('');
    setModal(true);
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (editing && !editing.canEdit) {
      setModal(false);
      return;
    }
    setBusy(true);
    try {
      const response = await apiRequest<{ status: NoteItem['status'] }>(
        editing ? `/api/v1/notes/${editing.id}` : '/api/v1/notes',
        {
          method: editing ? 'PATCH' : 'POST',
          accessToken: readToken(tenantTokenKey),
          body: JSON.stringify(form)
        }
      );
      setModal(false);
      setEditing(null);
      setNotice(response.status === 'PUBLISHED'
        ? 'Bilješka je odmah objavljena svim zaposlenicima.'
        : 'Bilješka je poslana mentoru, voditelju i administratoru na odobrenje.');
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Bilješku nije moguće spremiti.');
    } finally {
      setBusy(false);
    }
  }

  async function moderate(note: NoteItem, nextStatus: 'PUBLISHED' | 'REJECTED', rejectionReason = '') {
    setBusy(true);
    try {
      await apiRequest(`/api/v1/notes/${note.id}/moderation`, {
        method: 'PATCH',
        accessToken: readToken(tenantTokenKey),
        body: JSON.stringify({ status: nextStatus, reason: rejectionReason })
      });
      setRejecting(null);
      setReason('');
      setNotice(nextStatus === 'PUBLISHED'
        ? 'Bilješka je odobrena i sada je vidljiva svima.'
        : 'Bilješka je vraćena autoru s razlogom odbijanja.');
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Odluku nije moguće spremiti.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(note: NoteItem, event: MouseEvent) {
    event.stopPropagation();
    if (!window.confirm(`Obrisati bilješku „${note.title}”?`)) return;
    try {
      await apiRequest(`/api/v1/notes/${note.id}`, {
        method: 'DELETE',
        accessToken: readToken(tenantTokenKey)
      });
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Bilješku nije moguće obrisati.');
    }
  }

  return (
    <>
      <section className="note-summary task-summary">
        <article><MessageSquareText /><div><strong>{summary.published}</strong><small>Objavljene bilješke</small></div></article>
        <article><Clock3 /><div><strong>{summary.pending}</strong><small>Čekaju odobrenje</small></div></article>
        <article><FilePenLine /><div><strong>{summary.mine}</strong><small>Moje bilješke</small></div></article>
      </section>

      {notice && <button className="task-notice" onClick={() => setNotice('')}><Check /><span>{notice}</span><X /></button>}

      <section className="data-card note-data-card">
        <header className="data-toolbar note-toolbar">
          <label className="compact-search"><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Pretraži bilješke…" /></label>
          <select className="compact-select" value={status} onChange={(event) => setStatus(event.target.value as typeof status)}>
            <option value="ALL">Sve bilješke</option>
            <option value="PUBLISHED">Objavljene</option>
            <option value="PENDING">Čekaju odobrenje</option>
            <option value="REJECTED">Vraćene na doradu</option>
          </select>
          {canCreate && <button className="button button--primary" data-mobile-fab onClick={openNew} aria-label="Nova bilješka"><Plus /> Nova bilješka</button>}
        </header>
        {error && !modal && <div className="form-error panel-error">{error}</div>}
        <div className="note-list">
          {notes.map((note) => (
            <article
              key={note.id}
              className={`note-card note-card--${note.status.toLowerCase()}`}
              onClick={() => openNote(note)}
              onKeyDown={(event) => { if (event.key === 'Enter') openNote(note); }}
              role="button"
              tabIndex={0}
            >
              {note.canDelete && <button className="mobile-record-delete" onClick={(event) => void remove(note, event)} aria-label="Obriši bilješku"><Trash2 /></button>}
              <header>
                <span className={`status-pill status-pill--${note.status.toLowerCase()}`}>{noteStatusLabel(note.status)}</span>
                <time>{formatDateTimeValue(note.publishedAt ?? note.createdAt)}</time>
              </header>
              <h3>{note.title}</h3>
              <p>{note.body}</p>
              {note.rejectionReason && <div className="note-rejection"><strong>Razlog dorade:</strong> {note.rejectionReason}</div>}
              <footer>
                <span><UserRound /> {note.authorName}</span>
                {note.reviewerName && <span><ShieldCheck /> {note.reviewerName}</span>}
              </footer>
              {canModerate && note.status === 'PENDING' && (
                <div className="note-card__moderation">
                  <button className="button button--success button--small" onClick={(event) => { event.stopPropagation(); void moderate(note, 'PUBLISHED'); }}><Check /> Odobri</button>
                  <button className="button button--danger button--small" onClick={(event) => { event.stopPropagation(); setRejecting(note); }}><X /> Vrati na doradu</button>
                </div>
              )}
            </article>
          ))}
          {!loading && !notes.length && <div className="table-empty">Nema bilješki u ovom prikazu.</div>}
        </div>
      </section>

      {modal && (
        <div className="modal-backdrop">
          <form className="mini-modal note-modal" onSubmit={save}>
            <header><div><span className="eyebrow">{editing ? 'BILJEŠKA' : 'NOVA BILJEŠKA'}</span><h2>{editing ? editing.title : 'Objavi informaciju timu'}</h2></div><button type="button" className="icon-button" onClick={() => setModal(false)}><X /></button></header>
            <label className="field"><span>Naslov</span><input required minLength={2} value={form.title} readOnly={Boolean(editing && !editing.canEdit)} onChange={(event) => setForm({ ...form, title: event.target.value })} /></label>
            <label className="field"><span>Sadržaj bilješke</span><textarea required minLength={2} rows={8} value={form.body} readOnly={Boolean(editing && !editing.canEdit)} onChange={(event) => setForm({ ...form, body: event.target.value })} /></label>
            {editing?.rejectionReason && <div className="note-rejection modal-note-rejection"><strong>Razlog dorade:</strong> {editing.rejectionReason}</div>}
            {error && <div className="form-error">{error}</div>}
            <footer><button type="button" className="button button--ghost" onClick={() => setModal(false)}>Zatvori</button>{(!editing || editing.canEdit) && <button className="button button--primary" disabled={busy}>{busy ? 'Spremam…' : editing ? 'Spremi izmjene' : canModerate ? 'Objavi svima' : 'Pošalji na odobrenje'}</button>}</footer>
          </form>
        </div>
      )}

      {rejecting && (
        <div className="modal-backdrop modal-backdrop--nested">
          <form className="mini-modal" onSubmit={(event) => { event.preventDefault(); void moderate(rejecting, 'REJECTED', reason); }}>
            <header><div><span className="eyebrow">MODERIRANJE</span><h2>Vrati bilješku na doradu</h2></div><button type="button" className="icon-button" onClick={() => setRejecting(null)}><X /></button></header>
            <label className="field"><span>Jasan razlog za autora</span><textarea required minLength={2} rows={4} value={reason} onChange={(event) => setReason(event.target.value)} /></label>
            <footer><button type="button" className="button button--ghost" onClick={() => setRejecting(null)}>Odustani</button><button className="button button--danger" disabled={busy || !reason.trim()}>Vrati na doradu</button></footer>
          </form>
        </div>
      )}
    </>
  );
}
